import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Spinner } from '@components/ui';
import MilestoneModal from './MilestoneModal';
import { useProjectMilestones, useCreateMilestone, useUpdateMilestone, useDeleteMilestone } from '@hooks';
import { daysUntil, formatDay } from '@utils/calendarDate';

export default function MilestonesTimeline({ projectId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);

  const { data: milestonesData, isLoading } = useProjectMilestones(projectId);
  const createMilestone = useCreateMilestone();
  const updateMilestone = useUpdateMilestone();
  const deleteMilestone = useDeleteMilestone();

  const milestones = milestonesData?.data || [];

  // Separate past and upcoming (today's milestone is still upcoming)
  const upcomingMilestones = milestones.filter((m) => daysUntil(m.date) >= 0);
  const pastMilestones = milestones.filter((m) => daysUntil(m.date) < 0);

  const handleCreate = async (data) => {
    await createMilestone.mutateAsync(data);
    setIsModalOpen(false);
  };

  const handleUpdate = async (data) => {
    await updateMilestone.mutateAsync({
      id: editingMilestone.id,
      data,
    });
    setEditingMilestone(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este hito?')) {
      await deleteMilestone.mutateAsync(id);
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
  };

  const formatDate = (date) =>
    formatDay(date, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const getDaysUntil = (date) => {
    const diffDays = daysUntil(date);

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Mañana';
    if (diffDays < 0) return `Hace ${Math.abs(diffDays)} días`;
    if (diffDays <= 7) return `En ${diffDays} días`;
    if (diffDays <= 30) return `En ${Math.ceil(diffDays / 7)} semanas`;
    return `En ${Math.ceil(diffDays / 30)} meses`;
  };

  const renderMilestone = (milestone, isPast = false) => (
    <div
      key={milestone.id}
      style={{
        display: 'flex',
        gap: '16px',
        position: 'relative',
      }}
    >
      {/* Timeline dot */}
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: isPast ? '#f1f5f9' : '#667eea15',
          border: isPast ? '2px solid #e2e8f0' : '2px solid #667eea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {milestone.icon}
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          paddingBottom: '24px',
          opacity: isPast ? 0.7 : 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <h4
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0,
                textDecoration: isPast ? 'line-through' : 'none',
              }}
            >
              {milestone.name}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {formatDate(milestone.date)}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: isPast ? '#f1f5f9' : '#667eea15',
                  color: isPast ? '#64748b' : '#667eea',
                  fontWeight: '500',
                }}
              >
                {getDaysUntil(milestone.date)}
              </span>
            </div>
            {milestone.description && (
              <p style={{ fontSize: '13px', color: '#64748b', margin: '8px 0 0 0' }}>
                {milestone.description}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleEdit(milestone)}
              style={{
                padding: '6px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#64748b',
                cursor: 'pointer',
              }}
              title="Editar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              onClick={() => handleDelete(milestone.id)}
              style={{
                padding: '6px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#ef4444',
                cursor: 'pointer',
              }}
              title="Eliminar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
            Hitos del Proyecto
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
            {upcomingMilestones.length} próximos · {pastMilestones.length} pasados
          </p>
        </div>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          + Nuevo Hito
        </Button>
      </div>

      {milestones.length === 0 ? (
        <Card className="text-center py-12">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
          <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 16px 0' }}>
            No hay hitos definidos para este proyecto
          </p>
          <Button variant="primary" onClick={() => setIsModalOpen(true)}>
            Crear primer hito
          </Button>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Upcoming Milestones */}
          <Card padding="lg">
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#667eea', marginBottom: '20px' }}>
              Próximos Hitos ({upcomingMilestones.length})
            </h3>
            {upcomingMilestones.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '24px' }}>
                No hay hitos próximos
              </p>
            ) : (
              <div style={{ position: 'relative' }}>
                {/* Timeline line */}
                <div
                  style={{
                    position: 'absolute',
                    left: '19px',
                    top: '40px',
                    bottom: '24px',
                    width: '2px',
                    backgroundColor: '#667eea30',
                  }}
                />
                {upcomingMilestones.map((m) => renderMilestone(m, false))}
              </div>
            )}
          </Card>

          {/* Past Milestones */}
          <Card padding="lg">
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', marginBottom: '20px' }}>
              Hitos Pasados ({pastMilestones.length})
            </h3>
            {pastMilestones.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '24px' }}>
                No hay hitos pasados
              </p>
            ) : (
              <div style={{ position: 'relative' }}>
                {/* Timeline line */}
                <div
                  style={{
                    position: 'absolute',
                    left: '19px',
                    top: '40px',
                    bottom: '24px',
                    width: '2px',
                    backgroundColor: '#e2e8f0',
                  }}
                />
                {pastMilestones.map((m) => renderMilestone(m, true))}
              </div>
            )}
          </Card>
        </div>
      )}

      <MilestoneModal
        isOpen={isModalOpen || !!editingMilestone}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMilestone(null);
        }}
        onSubmit={editingMilestone ? handleUpdate : handleCreate}
        projectId={projectId}
        editData={editingMilestone}
        isLoading={createMilestone.isPending || updateMilestone.isPending}
      />
    </div>
  );
}

MilestonesTimeline.propTypes = {
  projectId: PropTypes.number.isRequired,
};
